import { useId, useMemo, useState } from "react";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Select, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import { NO_ANIMATION } from "./chart-ticks";
import { COLORS, formatCombinationLabel } from "./utils";
import type { SubjectCombinationRow, SubjectScoreRow } from "@/lib/statistics-api.service";

interface SubjectYearTrendChartProps {
	/** `undefined` = lần tải đầu chưa xong. */
	rows: SubjectScoreRow[] | undefined;
	/**
	 * Các tổ hợp để chọn, đã sắp giảm dần theo số thí sinh (server trả sẵn).
	 * `undefined` = chưa tải xong; mảng rỗng = không có tổ hợp nào để chọn.
	 */
	combinations: SubjectCombinationRow[] | undefined;
}

const TITLE = "Xu hướng từng môn qua ba năm";

const YEARS = [10, 11, 12] as const;

/**
 * Biến `rows` (một hàng = một môn, kèm `byYear`) thành dữ liệu recharts cần —
 * một hàng = một lớp, mỗi môn là một cột. `LineChart` vẽ mỗi dòng dữ liệu (một
 * lớp) như một điểm trên trục X, và mỗi `dataKey` (một môn) như một đường
 * riêng, nên phải xoay trục thế này trước khi truyền vào.
 */
export function pivotSubjectYearTrend(
	rows: readonly SubjectScoreRow[],
): Array<Record<string, string | number>> {
	return YEARS.map((year) => {
		const point: Record<string, string | number> = { year: `Lớp ${year}` };
		for (const row of rows) {
			const found = row.byYear.find((y) => y.year === year);
			if (found) point[row.subject] = found.avgScore;
		}
		return point;
	});
}

/**
 * Đường xu hướng điểm TB TỪNG MÔN (thang 10) qua ba năm lớp 10/11/12, mỗi môn
 * một đường — khác với `HocBaYearTrendChart` (pha 09) vốn chỉ vẽ một đường
 * chung cho cả tổ hợp. Mục đích: xem môn nào cải thiện nhiều/ít qua ba năm so
 * với các môn khác, không chỉ so với chính nó.
 *
 * Vẫn giữ nguyên chú thích quy chế của pha 09: điểm lớp 10 các môn Tin/Lý/Hoá/
 * Sinh có thể được thay bằng điểm Toán lớp 10 khi hồ sơ thiếu điểm gốc, nên
 * nhích lên một chút do quy đổi chứ không hoàn toàn là điểm thật của môn đó.
 */
export function SubjectYearTrendChart({ rows, combinations }: SubjectYearTrendChartProps) {
	// Rỗng = "chưa chọn" -> rơi về tổ hợp phổ biến nhất. KHÔNG khởi tạo state
	// bằng `combinations[0]` vì lần render đầu chưa có dữ liệu, và một
	// `useEffect` để đặt lại giá trị sau đó sẽ ghi đè lựa chọn của người dùng
	// mỗi lần bộ lọc trang làm danh sách tổ hợp tải lại.
	const [selected, setSelected] = useState("");
	const comboFilterId = `${useId()}-combo`;

	// Danh sách sắp A-Z THEO NHÃN hiển thị ("A00 - ...", rồi "B00 - ..."): đây
	// là một menu để tra, không phải một bảng xếp hạng — người dùng biết mình
	// cần tổ hợp nào và tìm nó theo mã.
	const options = useMemo(
		() =>
			[...(combinations ?? [])].sort((a, b) =>
				formatCombinationLabel(a.combination, a.code).localeCompare(
					formatCombinationLabel(b.combination, b.code),
					"vi",
				),
			),
		[combinations],
	);
	// Mặc định vẫn là tổ hợp ĐÔNG NHẤT (server trả sẵn thứ tự giảm dần), không
	// phải dòng đầu bảng chữ cái: mở tab lên là thấy ngay tổ hợp nói được nhiều
	// nhất về khoá này.
	const mostPopular = combinations?.[0];
	const activeCombination =
		options.find((c) => c.combination === selected) ?? mostPopular ?? options[0];
	const subjectsInCombination = useMemo(
		() => new Set(activeCombination?.subjects ?? []),
		[activeCombination],
	);

	if (rows === undefined) {
		return (
			<ChartCard title={TITLE} filename="xu-huong-tung-mon-qua-ba-nam">
				<Skeleton className="h-[360px] w-full" />
			</ChartCard>
		);
	}

	if (rows.length === 0) {
		return (
			<ChartCard title={TITLE} filename="xu-huong-tung-mon-qua-ba-nam">
				<p className="py-8 text-center text-sm text-muted-foreground">
					Chưa có dữ liệu xu hướng điểm theo môn trong phạm vi đang lọc.
				</p>
			</ChartCard>
		);
	}

	// Chỉ vẽ ba môn của tổ hợp đang chọn. Vẽ toàn bộ ~15 môn cùng lúc thì các
	// đường chồng lên nhau trong một dải điểm rất hẹp và Legend dài hơn cả biểu
	// đồ — không đọc được môn nào là môn nào.
	//
	// Không có tổ hợp nào (endpoint lỗi hoặc dữ liệu rỗng) thì vẽ hết như cũ,
	// hơn là hiện một biểu đồ trống.
	const visibleRows =
		subjectsInCombination.size > 0
			? rows.filter((row) => subjectsInCombination.has(row.subject))
			: rows;

	// Sắp theo `count` giảm dần: môn phổ biến nhất lên đầu Legend, dễ tìm hơn
	// thứ tự alphabet hay thứ tự server trả về ngẫu nhiên.
	const sortedSubjects = [...visibleRows].sort((a, b) => b.count - a.count);
	const data = pivotSubjectYearTrend(sortedSubjects);

	// Trục dọc CẮT theo dải dữ liệu thật, không chạy từ 0 — cùng lý do như
	// `HocBaYearTrendChart`: mọi mốc thật nằm trong khoảng 8,3-9,4 trên thang
	// 10, vẽ từ 0 thì 11 đường gần như chồng lên nhau thành một dải phẳng.
	const values = sortedSubjects.flatMap((row) => row.byYear.map((y) => y.avgScore));
	const axisMin = values.length
		? Math.max(0, Math.floor((Math.min(...values) - 0.2) * 10) / 10)
		: 0;
	const axisMax = values.length
		? Math.min(10, Math.ceil((Math.max(...values) + 0.2) * 10) / 10)
		: 10;

	return (
		<ChartCard
			title={TITLE}
			filename="xu-huong-tung-mon-qua-ba-nam"
			description="Điểm trung bình từng môn, tính riêng theo từng lớp — mỗi môn một đường"
		>
			{options.length > 0 && (
				<div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
					<label
						htmlFor={comboFilterId}
						className="text-xs font-medium text-muted-foreground whitespace-nowrap"
					>
						Tổ hợp:
					</label>
					<Select
						id={comboFilterId}
						value={activeCombination?.combination ?? ""}
						onChange={(e) => setSelected(e.target.value)}
						className="h-9 w-full text-xs sm:w-96"
					>
						{options.map((option) => (
							<SelectItem key={option.combination} value={option.combination}>
								{`${formatCombinationLabel(option.combination, option.code)} (${option.total.toLocaleString("vi-VN")})`}
							</SelectItem>
						))}
					</Select>
				</div>
			)}
			<ResponsiveContainer width="100%" height={360}>
				<LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
					<CartesianGrid strokeDasharray="3 3" />
					<XAxis dataKey="year" />
					<YAxis domain={[axisMin, axisMax]} allowDecimals />
					<Tooltip formatter={(value) => (typeof value === "number" ? value.toFixed(2) : "")} />
					<Legend wrapperStyle={{ fontSize: 12 }} />
					{sortedSubjects.map((row, index) => (
						<Line
							key={row.subject}
							{...NO_ANIMATION}
							type="monotone"
							dataKey={row.subject}
							stroke={COLORS[index % COLORS.length]}
							strokeWidth={2}
							dot={{ r: 3 }}
							connectNulls
						/>
					))}
				</LineChart>
			</ResponsiveContainer>

			<p className="mt-3 text-xs text-muted-foreground">
				Trục dọc bắt đầu từ {axisMin} chứ không từ 0 (thang đầy đủ là 10): các mốc thật đều nằm
				trong một dải hẹp, vẽ từ 0 thì mọi đường phẳng lì và không thấy được xu hướng.
			</p>
			<p className="mt-2 text-xs text-muted-foreground">
				Quy chế cho phép thay điểm lớp 10 các môn Tin học/Vật lí/Hoá học/Sinh học bằng điểm Toán lớp
				10 khi hồ sơ thiếu điểm gốc, nên điểm lớp 10 của các môn này có thể nhích lên một chút do
				quy đổi chứ không hoàn toàn phản ánh điểm thật.
			</p>
		</ChartCard>
	);
}
